import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Loader2 } from 'lucide-react';
import { toast } from 'sonner';
import { api } from '../../api/client';
import { unwrap } from '../../lib/api-envelope';
import { LOAD_FAILURE_QUERY_OPTIONS } from '../../lib/load-failure';
import { LoadFailureNotice } from '../../components/ui/LoadFailureNotice';
import ConfirmModal from '../../components/ui/ConfirmModal';
import { describeChange, exampleDate, parseAiRules, type AiRule } from '../../lib/ai-rules';
import { useTenantTimeZone } from '../../hooks/useTenantTimeZone';
import { billingFlagTypeLabel } from '../../lib/billing-agent';

/**
 * Settings → AI & integrations → Learned rules (API 0422).
 *
 * FieldIQ PROPOSES a rule once the same fix has been made to three AI ticket
 * drafts for one customer; an admin activates it, and only an active rule
 * changes what AI prefill proposes (a rate rule is enforced on the draft).
 * Two sections, both from the one GET: Suggested (proposed) with Activate /
 * Dismiss, Active with Delete. Dismissed rules are on the payload and are not
 * shown — neither section is theirs.
 *
 * Three states, never two: a failed or refused load renders LoadFailureNotice
 * where the empty state would be. The routes are strict admin, so an `owner`
 * (whom the tab's adminOnly flag admits) reads a refusal here, not "no rules".
 *
 * No rate-sheet price beside a rate rule: the API does not return one, and this
 * page does not derive it (see lib/ai-rules).
 */

export const AI_RULES_QUERY_KEY = ['ai-rules'];

export const EMPTY_COPY = 'No rules yet — FieldIQ suggests one after the same fix is made 3 times.';

type Action = 'activate' | 'dismiss' | 'delete';

/**
 * What a rule's count counts. A billing_preflight rule (api#916) is backed by
 * DISMISSED FLAGS, not corrections to a draft, and saying "corrections" there
 * would describe something the office never did.
 */
function backingLabel(rule: AiRule): string {
  const [one, many] = rule.kind === 'billing_preflight' ? ['dismissal', 'dismissals'] : ['correction', 'corrections'];
  const n = rule.correctionCount;
  if (n === null) return `— ${many}`;
  return `${n} ${n === 1 ? one : many}`;
}

function RuleCard({
  rule,
  actions,
}: {
  rule: AiRule;
  actions: React.ReactNode;
}) {
  const timeZone = useTenantTimeZone();
  return (
    <li data-testid={`ai-rule-${rule.id}`} className="px-4 py-4 space-y-3">
      <div className="flex flex-col gap-3 md:flex-row md:items-start md:justify-between">
        <div className="min-w-0 space-y-1">
          {/* A billing rule's line type is the constant 'other' — never shown. Its field names the check. */}
          {rule.kind === 'billing_preflight' && (
            <p className="text-xs font-semibold text-fieldiq-muted" data-testid="ai-rule-check">
              Billing check: {billingFlagTypeLabel(rule.field)}
            </p>
          )}
          <p className="text-sm font-medium text-white">{rule.ruleText}</p>
          <p className="text-xs text-fieldiq-muted">Backed by {backingLabel(rule)}</p>
        </div>
        <div className="flex shrink-0 gap-2">{actions}</div>
      </div>
      {rule.examples.length > 0 && (
        <ul aria-label="Example corrections" className="space-y-1">
          {rule.examples.slice(0, 3).map((ex) => {
            const change = describeChange(rule.field, ex.before, ex.after);
            return (
              <li key={ex.id} className="text-xs text-fieldiq-muted flex flex-wrap gap-x-2">
                <span className="text-fieldiq-text">{change.before} → {change.after}</span>
                <span>{exampleDate(ex.createdAt, timeZone)}</span>
                {ex.ticketNumber && <span>{ex.ticketNumber}</span>}
              </li>
            );
          })}
        </ul>
      )}
    </li>
  );
}

const BTN = 'px-3 py-1.5 rounded-lg text-xs font-semibold transition-colors disabled:opacity-50';
const BTN_SECONDARY = `${BTN} bg-fieldiq-card border border-fieldiq-border text-white hover:border-fieldiq-accent`;

export default function LearnedRulesPage() {
  const queryClient = useQueryClient();
  const [confirmDelete, setConfirmDelete] = useState<AiRule | null>(null);

  const { data, isLoading, error } = useQuery({
    queryKey: AI_RULES_QUERY_KEY,
    queryFn: () => api.aiRules.list().then((r) => parseAiRules(unwrap(r.data))),
    ...LOAD_FAILURE_QUERY_OPTIONS,
  });

  const mutation = useMutation({
    mutationFn: ({ id, action }: { id: string; action: Action }) =>
      action === 'activate' ? api.aiRules.activate(id)
        : action === 'dismiss' ? api.aiRules.dismiss(id)
        : api.aiRules.delete(id),
    onSuccess: (_res, { action }) => {
      toast.success(action === 'activate' ? 'Rule activated' : action === 'dismiss' ? 'Rule dismissed' : 'Rule deleted');
      setConfirmDelete(null);
    },
    onError: (err: any) => toast.error(err?.response?.data?.error || 'Failed to update the rule'),
    // A 409 means the rule moved under us; refetch either way.
    onSettled: () => queryClient.invalidateQueries({ queryKey: AI_RULES_QUERY_KEY }),
  });

  const busy = (id: string) => mutation.isPending && mutation.variables?.id === id;

  if (isLoading) {
    return <div className="flex items-center justify-center py-20"><Loader2 className="w-6 h-6 text-fieldiq-accent animate-spin" /></div>;
  }

  const rules = data ?? [];
  const suggested = rules.filter((r) => r.status === 'proposed');
  const active = rules.filter((r) => r.status === 'active');

  return (
    <div className="min-h-full">
      {/* The AI & integrations workspace renders the title and the tab strip. */}
      <div className="px-6 py-4 space-y-6">
        <p className="text-xs text-fieldiq-muted">
          Rules come from the fixes your office makes to AI ticket drafts, and from billing checks the office dismisses. Only an active rule changes what the AI proposes or flags.
        </p>

        {error ? (
          <LoadFailureNotice error={error} subject="learned rules" allowed="admins" />
        ) : suggested.length === 0 && active.length === 0 ? (
          <p className="text-sm text-fieldiq-muted" data-testid="ai-rules-empty">{EMPTY_COPY}</p>
        ) : (
          <>
            <section aria-labelledby="ai-rules-suggested" className="space-y-2">
              <h2 id="ai-rules-suggested" className="text-sm font-semibold text-white">
                Suggested <span className="text-fieldiq-muted font-normal">({suggested.length})</span>
              </h2>
              {suggested.length === 0 ? (
                <p className="text-xs text-fieldiq-muted">No suggested rules.</p>
              ) : (
                <ul className="bg-fieldiq-card border border-fieldiq-border rounded-xl divide-y divide-fieldiq-border">
                  {suggested.map((rule) => (
                    <RuleCard
                      key={rule.id}
                      rule={rule}
                      actions={
                        <>
                          <button
                            type="button"
                            className={`${BTN} bg-fieldiq-accent text-fieldiq-dark hover:bg-amber-400`}
                            disabled={busy(rule.id)}
                            onClick={() => mutation.mutate({ id: rule.id, action: 'activate' })}
                          >
                            Activate
                          </button>
                          <button
                            type="button"
                            className={BTN_SECONDARY}
                            disabled={busy(rule.id)}
                            onClick={() => mutation.mutate({ id: rule.id, action: 'dismiss' })}
                          >
                            Dismiss
                          </button>
                        </>
                      }
                    />
                  ))}
                </ul>
              )}
            </section>

            <section aria-labelledby="ai-rules-active" className="space-y-2">
              <h2 id="ai-rules-active" className="text-sm font-semibold text-white">
                Active <span className="text-fieldiq-muted font-normal">({active.length})</span>
              </h2>
              {active.length === 0 ? (
                <p className="text-xs text-fieldiq-muted">No active rules.</p>
              ) : (
                <ul className="bg-fieldiq-card border border-fieldiq-border rounded-xl divide-y divide-fieldiq-border">
                  {active.map((rule) => (
                    <RuleCard
                      key={rule.id}
                      rule={rule}
                      actions={
                        <button
                          type="button"
                          className={BTN_SECONDARY}
                          disabled={busy(rule.id)}
                          onClick={() => setConfirmDelete(rule)}
                        >
                          Delete
                        </button>
                      }
                    />
                  ))}
                </ul>
              )}
            </section>
          </>
        )}
      </div>

      <ConfirmModal
        isOpen={confirmDelete !== null}
        title="Delete this rule?"
        body={confirmDelete ? `“${confirmDelete.ruleText}” stops applying to AI prefill.` : ''}
        confirmLabel="Delete rule"
        loading={mutation.isPending}
        onConfirm={() => confirmDelete && mutation.mutate({ id: confirmDelete.id, action: 'delete' })}
        onCancel={() => setConfirmDelete(null)}
      />
    </div>
  );
}
